import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateEventForm from '../_components/CreateEventForm/CreateEventForm';
import InvitationPreviewV2 from '../_components/InvitationPreview/InvitationPreviewV2';
import type { Event, Guest } from '../_graphql/generated/types';
import { AttendanceStatus } from '../_graphql/generated/types';
import { eventsApi, guestsApi } from '../_lib/api-client';
import { InvitationDesign } from '../_types/invitation-design-v2';

type EventStats = {
  total: number;
  invited: number;
  attending: number;
  notAttending: number;
  noResponse: number;
};

type EventWithStats = Event & {
  stats?: EventStats;
};

type State =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'loaded'; events: EventWithStats[] };

const EventsList: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [onboardingDesign, setOnboardingDesign] = useState<InvitationDesign | null>(null);
  const [onboardingEventName, setOnboardingEventName] = useState<string>('');
  const [onboardingEventDate, setOnboardingEventDate] = useState<string>('');
  const [isCreatingFromOnboarding, setIsCreatingFromOnboarding] = useState(false);
  
  // Check URL for create param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('create') === 'true') {
      setShowCreateForm(true);
      // Remove the param from URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchEvents = async () => {
    setState({ status: 'loading' });
    try {
      const eventSummaries = await eventsApi.getAll();
      
      // Fetch full event data including invitation types and guest stats
      const eventsWithStats = await Promise.all(
        eventSummaries.map(async (eventSummary) => {
          try {
            // Get full event data with invitation types
            const fullEvent = await eventsApi.getById(eventSummary.id);
            
            if (!fullEvent) {
              return eventSummary;
            }
            
            // Get guest stats
            const guests = await guestsApi.getByEvent(fullEvent.id);
            
            const stats: EventStats = {
              total: guests.length,
              invited: guests.filter(g => g.invitationId).length,
              attending: guests.filter(g => g.invitationId && g.attendance === AttendanceStatus.ATTENDING).length,
              notAttending: guests.filter(g => g.invitationId && g.attendance === AttendanceStatus.NOT_ATTENDING).length,
              noResponse: guests.filter(g => g.invitationId && g.attendance === AttendanceStatus.NO_RESPONSE).length
            };
            
            return { ...fullEvent, stats };
          } catch (e) {
            // If we can't fetch full data, return summary
            console.error(`Failed to fetch data for event ${eventSummary.id}:`, e);
            return eventSummary;
          }
        })
      );
      
      // Sort events: future events ascending, past events descending
      const now = new Date().getTime();
      const sortedEvents = eventsWithStats.sort((a, b) => {
        const aTime = new Date(a.eventDateTime).getTime();
        const bTime = new Date(b.eventDateTime).getTime();
        const aIsFuture = aTime >= now;
        const bIsFuture = bTime >= now;
        
        // If one is future and one is past, future comes first
        if (aIsFuture && !bIsFuture) return -1;
        if (!aIsFuture && bIsFuture) return 1;
        
        // If both are future, sort ascending (earliest first)
        if (aIsFuture && bIsFuture) {
          return aTime - bTime;
        }
        
        // If both are past, sort descending (most recent first)
        return bTime - aTime;
      });
      
      setState({ status: 'loaded', events: sortedEvents });
    } catch (e) {
      setState({ status: 'error', error: 'Failed to load events' });
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // Check for onboarding data after events are loaded
  useEffect(() => {
    if (state.status !== 'loaded') return;
    
    const storedData = localStorage.getItem('onboarding-data');
    console.log('🔍 EventsList checking for onboarding data:', storedData ? 'Found' : 'Not found');
    
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        console.log('📦 Parsed onboarding data:', data);
        
        if (data.design && data.eventName && data.eventDate) {
          // If we have complete onboarding data, create the event automatically
          console.log('🎯 Auto-creating event from onboarding data');
          setOnboardingDesign(data.design);
          setOnboardingEventName(data.eventName);
          setOnboardingEventDate(data.eventDate);
          setIsCreatingFromOnboarding(true);
          
          // Auto-create the event
          handleCreateEventWithDesign(data.eventName, data.eventDate, data.design);
          
          // Clear onboarding data immediately since we're creating the event
          localStorage.removeItem('onboarding-data');
          localStorage.removeItem('onboarding-design');
        }
      } catch (e) {
        console.error('Failed to parse onboarding data:', e);
      }
    }
  }, [state.status]);

  const handleCreateEventWithDesign = async (eventName: string, eventDate: string, design: InvitationDesign) => {
    setIsSubmitting(true);
    setMessage(null);
    
    try {
      const eventDateTime = new Date(eventDate).getTime();
      const newEvent = await eventsApi.create({ 
        eventName, 
        eventDateTime,
        defaultInvitationDesign: design
      });
      
      setMessage({ type: 'success', text: 'Event created successfully with your invitation design!' });
      
      // Navigate to the new event
      const eventNameSlug = eventName.toLowerCase().replace(/\s+/g, '-');
      navigate(`/app/events/${newEvent.code}/${eventNameSlug}`);
      
      // Clear onboarding data after successful event creation
      localStorage.removeItem('onboarding-data');
      localStorage.removeItem('onboarding-design');
      console.log('✅ Cleared onboarding data after successful event creation');
      
      // Refresh events list
      fetchEvents();
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to create event' });
      setIsSubmitting(false);
      setIsCreatingFromOnboarding(false);
    }
  };

  const handleCreateEvent = async (eventName: string, eventDate: string) => {
    setIsSubmitting(true);
    setMessage(null);
    
    try {
      const eventDateTime = new Date(eventDate).getTime();
      const newEvent = await eventsApi.create({ 
        eventName, 
        eventDateTime
      });
      
      setMessage({ type: 'success', text: 'Event created successfully!' });
      
      // Navigate to the new event
      const eventNameSlug = eventName.toLowerCase().replace(/\s+/g, '-');
      navigate(`/app/events/${newEvent.code}/${eventNameSlug}`);
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to create event' });
      setIsSubmitting(false);
    }
  };

  const handleEventClick = (event: Event) => {
    const eventNameSlug = event.eventName.toLowerCase().replace(/\s+/g, '-');
    navigate(`/app/events/${event.code}/${eventNameSlug}`);
  };

  // Show loading screen when creating from onboarding
  if (isCreatingFromOnboarding) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Creating Your Event</h2>
          <p className="text-gray-600">
            We're setting up your event with the beautiful invitation design you created...
          </p>
        </div>
      </div>
    );
  }

  if (state.status === 'loading') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="text-center py-8 text-red-600">
        <p className="text-xl font-medium mb-2">Error</p>
        <p>{state.error}</p>
      </div>
    );
  }

  if (showCreateForm) {
    return (
      <div className="flex-1 p-4 sm:p-6 lg:p-10 bg-gray-50 font-sans relative">
        {/* Mixed pink and green gradient background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-bl from-emerald-300/50 via-green-200/40 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute top-1/3 -left-20 w-80 h-80 bg-gradient-to-tr from-rose-300/50 via-pink-200/40 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-gradient-to-tl from-teal-300/40 via-emerald-200/30 to-transparent rounded-full blur-2xl"></div>
        </div>
        
        {/* Content wrapper to ensure it's above the gradient */}
        <div className="relative z-10">
          <CreateEventForm
            onSubmit={handleCreateEvent}
            onCancel={() => setShowCreateForm(false)}
            isSubmitting={isSubmitting}
          />
          {message && (
            <div className={`fixed top-4 right-4 sm:top-8 sm:right-8 px-4 sm:px-6 py-3 sm:py-4 rounded-lg font-medium z-50 animate-slide-in ${
              message.type === 'success' 
                ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' 
                : 'bg-red-50 text-red-600 border border-red-200'
            } text-sm sm:text-base shadow-lg`}>
              {message.text}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 sm:p-6 lg:p-10 bg-gray-50 font-sans relative">
      {/* Mixed pink and green gradient background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-bl from-emerald-300/50 via-green-200/40 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 -left-20 w-80 h-80 bg-gradient-to-tr from-rose-300/50 via-pink-200/40 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-gradient-to-tl from-teal-300/40 via-emerald-200/30 to-transparent rounded-full blur-2xl"></div>
      </div>
      
      {/* Content wrapper to ensure it's above the gradient */}
      <div className="relative z-10">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">My Events</h1>
        <button
          onClick={() => navigate('/design')}
          className="px-4 py-2 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-all"
        >
          Create Event
        </button>
      </div>

      {state.events.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200">
          <div className="mb-6">
            <svg 
              className="mx-auto h-16 w-16 text-gray-400" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No events yet</h3>
          <p className="text-gray-500 mb-6">Create your first event to get started</p>
          <button
            onClick={() => navigate('/design')}
            className="px-6 py-3 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-full hover:shadow-lg transition-all font-medium"
          >
            Create Your First Event
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 mb-6">
          {state.events.map((ev) => (
            <div key={ev.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-all hover:shadow-xl flex flex-col">
              {/* Invitation Thumbnail */}
              <div 
                className="h-64 bg-gray-100 relative overflow-hidden cursor-pointer"
                onClick={() => handleEventClick(ev)}
              >
                {ev.invitationTypes && ev.invitationTypes.length > 0 && ev.invitationTypes[0].designCustomizations ? (
                  <div className="absolute inset-0">
                    <InvitationPreviewV2
                      design={ev.invitationTypes[0].designCustomizations as InvitationDesign}
                      event={ev}
                    />
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                    <div className="text-center">
                      <svg className="w-16 h-16 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                      <p className="text-gray-500 font-medium">Design in Progress</p>
                      <p className="text-gray-400 text-sm mt-1">Create your invitation design</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{ev.eventName}</h3>
                  <div className="flex items-center text-gray-600 mb-4">
                    <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {new Date(ev.eventDateTime).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                
                  {/* Guest Statistics */}
                  {ev.stats && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div className="text-center">
                          <div className="font-bold text-gray-800">{ev.stats.total}</div>
                          <div className="text-xs text-gray-500">Total</div>
                        </div>
                        <div className="text-center">
                          <div className="font-bold text-blue-600">{ev.stats.invited}</div>
                          <div className="text-xs text-gray-500">Invited</div>
                        </div>
                        <div className="text-center">
                          <div className="font-bold text-green-600">{ev.stats.attending}</div>
                          <div className="text-xs text-gray-500">Attending</div>
                        </div>
                      </div>
                      {ev.stats.invited > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <div className="flex justify-between text-xs text-gray-600">
                            <span>Response Rate</span>
                            <span className="font-medium">
                              {Math.round(((ev.stats.attending + ev.stats.notAttending) / ev.stats.invited) * 100)}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <button 
                  className="w-full px-4 py-2 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-full font-medium hover:shadow-md transition-all mt-auto"
                  onClick={() => handleEventClick(ev)}
                >
                  View Event
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Success/Error Messages */}
      {message && (
        <div className={`fixed top-20 right-4 px-6 py-3 rounded-lg font-medium z-50 animate-slide-in ${
          message.type === 'success' 
            ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' 
            : 'bg-red-50 text-red-600 border border-red-200'
        } shadow-lg`}>
          {message.text}
        </div>
      )}
      </div>
    </div>
  );
};

export default EventsList;